import { getTrackingParentAttributeValue, getTrackingParents, PARENT_ATTRIBUTE } from './parent';

const LABEL_ATTRIBUTE = 'data-tracking-label';
const LABEL_ATTRIBUTE_NO_CONTENT = 'data-tracking-label-no-content';

const isTrackingLabel = (target: EventTarget): target is HTMLElement => {
  if (target instanceof Document || target instanceof Window) {
    return false;
  }

  const element = target as HTMLElement;

  return element.hasAttribute(LABEL_ATTRIBUTE);
};

const isTrackingLabelWithNoContent = (element: HTMLElement): boolean => {
  return isTrackingLabel(element) && element.hasAttribute(LABEL_ATTRIBUTE_NO_CONTENT);
};

const findTrackingLabel = (parentElement: Element): HTMLElement | null => {
  if (!parentElement) return null;

  const parentType: string = getTrackingParentAttributeValue(parentElement);
  let labelElement: HTMLElement | null;

  // find a first child that is a tracking-label, but:
  // - ignore ones that are children of a different nested tracking parent
  // - ignore ones that are nested parent and label at the same time (effectively a label for a nested parent))
  try {
    labelElement = parentElement.querySelector(
      `[${LABEL_ATTRIBUTE}]` +
        `:not([${PARENT_ATTRIBUTE}=${parentType}] [${PARENT_ATTRIBUTE}] [${LABEL_ATTRIBUTE}])` +
        `:not([${PARENT_ATTRIBUTE}=${parentType}] [${PARENT_ATTRIBUTE}][${LABEL_ATTRIBUTE}])`
    );
  } catch {
    labelElement = null;
  }

  // check if parent is not a label itself
  if (!labelElement && isTrackingLabel(parentElement)) {
    return parentElement;
  }

  return labelElement;
};

const getLabelAttributeValue = (labelElement: Element): string => {
  const attributeValue: string | null = labelElement.getAttribute(LABEL_ATTRIBUTE);

  if (!attributeValue) {
    return '';
  }

  return attributeValue !== 'true' ? attributeValue : '';
};

const getTrackingLabelValue = (labelElement: HTMLElement | null): string => {
  if (!labelElement) {
    return '';
  }

  let label;

  // if label was marked explicitly as one without content, we will not use its innerText
  if (!isTrackingLabelWithNoContent(labelElement)) {
    label = labelElement.innerText;
  }

  return label || getLabelAttributeValue(labelElement);
};

export const createTrackingLabel = (nativeEvent: Event, includeParentType: boolean = false): string => {
  return getTrackingParents(nativeEvent)
    .map((parentElement: Element) => {
      const labels: string[] = [];
      const labelElement: HTMLElement | null = findTrackingLabel(parentElement);
      const labelText: string = getTrackingLabelValue(labelElement);

      if (includeParentType) {
        labels.push(getTrackingParentAttributeValue(parentElement));
      }
      if (labelText) {
        labels.push(labelText);
      }

      return labels.join(':');
    })
    .filter(Boolean)
    .reverse()
    .join('|');
};

export const createTrackingLabelAttribute = (
  label?: string,
  options?: { ignoreContent: boolean }
): {
  [key: string]: boolean | string;
} => {
  const attributes: {
    [key: string]: boolean | string;
  } = {};

  attributes[`${LABEL_ATTRIBUTE}`] = label || true;
  if (options?.ignoreContent) {
    attributes[`${LABEL_ATTRIBUTE_NO_CONTENT}`] = true;
  }

  return attributes;
};
